import React, { useState, useEffect } from 'react'
import { ArrowLeft, Save, RefreshCw, Settings, Mail, Users, FileText, TestTube, AlertCircle, CheckCircle, ChevronDown, ChevronUp, BarChart3 } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { EmailConfigSection } from './EmailConfigSection'
import { EmailReportsSection } from './EmailReportsSection'
import { ReportStatusSection } from './ReportStatusSection'
import { useAuth } from '../../hooks/useAuth'
import { Header } from '../Layout/Header'

// Accordion Component
const SettingsAccordion: React.FC<{
  title: string
  icon: React.ReactNode
  children: React.ReactNode
  defaultOpen?: boolean
}> = ({ title, icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div className="card mb-4 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 sm:p-5 min-h-touch transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="text-kvh-red">{icon}</span>
          <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</h2>
        </div>
        <div className="text-gray-500">
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>
      {isOpen && (
        <div className="p-4 sm:p-6 border-t" style={{ borderColor: 'var(--border)', background: 'var(--surface-muted)' }}>
          {children}
        </div>
      )}
    </div>
  )
}

interface SettingsState {
  reportsEnabled: boolean
  sendTime: string
  weeklyReportsEnabled: boolean
  lastDailyReport: string | null
  lastWeeklyReport: string | null
  emailServiceId: string
  emailTemplateId: string
  emailWeeklyTemplateId: string
  emailPublicKey: string
  emailJSId: string | null  // UUID of the emailJS record
  isLoaded: boolean
}

interface StatusMessage {
  type: 'success' | 'error' | 'info'
  message: string
}

export const SettingsPage: React.FC = () => {
  const { user, logout } = useAuth()
  const [settings, setSettings] = useState<SettingsState>({
    reportsEnabled: false,
    sendTime: '22:00',
    weeklyReportsEnabled: false,
    lastDailyReport: null,
    lastWeeklyReport: null,
    emailServiceId: 'service_0l8qkrk',
    emailTemplateId: 'template_0k6h5el',
    emailWeeklyTemplateId: 'template_0k6h5el',
    emailPublicKey: 'XTBroP4UexB2siL7F',
    emailJSId: null,
    isLoaded: false
  })
  
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null)

  useEffect(() => {
    loadSettings()
  }, [])

  const showMessage = (type: 'success' | 'error' | 'info', message: string) => {
    setStatusMessage({ type, message })
    setTimeout(() => setStatusMessage(null), 5000)
  }

  const loadSettings = async () => {
    try {
      setIsLoading(true)
      console.log('🔄 Loading settings from database...')

      // Load settings from your existing settings table structure
      // Use maybeSingle() to handle 0 or 1 results without error
      const { data: settingsArray, error: settingsError } = await supabase
        .from('settings')
        .select('*')
        .or('category.eq.email_reports,type.eq.email_reports')
        .order('updated_at', { ascending: false })
        .limit(1)

      const settingsData = settingsArray?.[0] || null

      if (settingsError) {
        console.error('❌ Error loading settings:', settingsError)
      }

      // Load EmailJS configuration from emailJS table (get first record, don't filter by id)
      const { data: emailJSArray, error: emailJSError } = await supabase
        .from('emailJS')
        .select('*')
        .limit(1)

      const emailJSData = emailJSArray?.[0] || null

      if (emailJSError) {
        console.error('❌ Error loading emailJS config:', emailJSError)
      }

      console.log('📧 EmailJS data loaded:', emailJSData)

      // Combine settings from both sources
      let config: Record<string, any> = {}
      if (settingsData) {
        config = settingsData.config || settingsData.settings_json || {}
      }

      setSettings(prev => ({
        ...prev,
        reportsEnabled: config.reportsEnabled || false,
        sendTime: config.sendTime || '22:00',
        weeklyReportsEnabled: config.weeklyReportsEnabled || false,
        emailServiceId: emailJSData?.serviceID || 'service_0l8qkrk',
        emailTemplateId: emailJSData?.DailyTemplateID || 'template_0k6h5el',
        emailWeeklyTemplateId: emailJSData?.WeeklyTemplateID || 'template_0k6h5el',
        emailPublicKey: emailJSData?.PublicKey || 'XTBroP4UexB2siL7F',
        emailJSId: emailJSData?.id || null,
        isLoaded: true
      }))

      // Load report status
      const { data: reportsData, error: reportsError } = await supabase
        .from('email_reports_log')
        .select('*')
        .order('sent_at', { ascending: false })
        .limit(10)

      if (reportsError) {
        console.warn('⚠️ Error loading reports log:', reportsError)
      } else {
        const dailyReports = reportsData?.filter(r => r.type === 'daily') || []
        const weeklyReports = reportsData?.filter(r => r.type === 'weekly') || []
        
        setSettings(prev => ({
          ...prev,
          lastDailyReport: dailyReports[0]?.sent_at || null,
          lastWeeklyReport: weeklyReports[0]?.sent_at || null
        }))
      }

      console.log('✅ Settings loaded successfully')
      showMessage('success', 'Instellingen geladen')
    } catch (error) {
      console.error('❌ Error loading settings:', error)
      showMessage('error', 'Fout bij het laden van instellingen')
    } finally {
      setIsLoading(false)
    }
  }

  const saveSettings = async () => {
    try {
      setIsSaving(true)
      console.log('💾 Saving settings to database...')

      const settingsConfig = {
        reportsEnabled: settings.reportsEnabled,
        sendTime: settings.sendTime,
        weeklyReportsEnabled: settings.weeklyReportsEnabled,
        updatedAt: new Date().toISOString(),
        updatedBy: user?.id
      }

      // First check if a record with type 'email_reports' exists
      const { data: existingSettings } = await supabase
        .from('settings')
        .select('id')
        .eq('type', 'email_reports')
        .limit(1)
        .single()

      let settingsError
      if (existingSettings?.id) {
        // Update existing record
        const { error } = await supabase
          .from('settings')
          .update({
            category: 'reporting',
            config: settingsConfig,
            updated_at: new Date().toISOString(),
            updated_by: user?.name || 'System'
          })
          .eq('type', 'email_reports')
        settingsError = error
      } else {
        // Insert new record (let database generate UUID)
        const { error } = await supabase
          .from('settings')
          .insert({
            category: 'reporting',
            type: 'email_reports',
            config: settingsConfig,
            updated_at: new Date().toISOString(),
            updated_by: user?.name || 'System'
          })
        settingsError = error
      }

      if (settingsError) {
        console.error('❌ Error saving settings:', settingsError)
        showMessage('error', 'Fout bij het opslaan van instellingen')
        return
      }

      // Save EmailJS config to separate table
      let emailJSError
      if (settings.emailJSId) {
        // Update existing record using the stored UUID
        const { error } = await supabase
          .from('emailJS')
          .update({
            serviceID: settings.emailServiceId,
            PublicKey: settings.emailPublicKey,
            DailyTemplateID: settings.emailTemplateId,
            WeeklyTemplateID: settings.emailWeeklyTemplateId
          })
          .eq('id', settings.emailJSId)
        emailJSError = error
      } else {
        // Insert new record (let database generate UUID)
        const { error } = await supabase
          .from('emailJS')
          .insert({
            serviceID: settings.emailServiceId,
            PublicKey: settings.emailPublicKey,
            DailyTemplateID: settings.emailTemplateId,
            WeeklyTemplateID: settings.emailWeeklyTemplateId
          })
        emailJSError = error
      }

      if (emailJSError) {
        console.error('❌ Error saving EmailJS config:', emailJSError)
        showMessage('error', 'Fout bij het opslaan van EmailJS configuratie')
        return
      }

      console.log('✅ Settings saved successfully')
      showMessage('success', 'Instellingen opgeslagen!')
    } catch (error) {
      console.error('❌ Error saving settings:', error)
      showMessage('error', 'Fout bij het opslaan van instellingen')
    } finally {
      setIsSaving(false)
    }
  }

  const resetSettings = () => {
    setSettings(prev => ({
      reportsEnabled: false,
      sendTime: '22:00',
      weeklyReportsEnabled: false,
      lastDailyReport: null,
      lastWeeklyReport: null,
      emailServiceId: 'service_0l8qkrk',
      emailTemplateId: 'template_0k6h5el',
      emailWeeklyTemplateId: 'template_0k6h5el',
      emailPublicKey: 'XTBroP4UexB2siL7F',
      emailJSId: prev.emailJSId, // Preserve the UUID
      isLoaded: true
    }))
    showMessage('info', 'Instellingen gereset naar standaard waarden')
  }

  const handleBackToDashboard = () => {
    // Navigate to the appropriate dashboard based on user role
    const roleRoutes = {
      admin: '/dashboard/admin',
      sales: '/dashboard/sales',
      teamleader: '/dashboard/teamleader',
      forklift: '/dashboard/forklift',
      technical: '/dashboard/technical'
    }
    
    const targetRoute = user?.role ? roleRoutes[user.role] : '/dashboard/admin'
    window.location.href = targetRoute
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg)' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kvh-red mx-auto mb-4"></div>
          <p style={{ color: 'var(--text-secondary)' }}>Instellingen laden...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)' }}>
      <Header title="Instellingen" onLogout={() => { logout(); window.location.href = '/login' }} />

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-3 sm:px-6 py-6 sm:py-8">
        <button onClick={handleBackToDashboard} className="btn-ghost !px-0 mb-4"><ArrowLeft size={17} /> Terug naar dashboard</button>
        <div className="mb-6 sm:mb-8">
          <p className="eyebrow">Beheer</p>
          <h1 className="page-title mt-2">Systeeminstellingen</h1>
          <p className="page-subtitle mt-1">Beheer rapportages, e-mailkoppelingen en verzendmomenten.</p>
        </div>
        {/* Status Messages */}
        {statusMessage && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            statusMessage.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
            statusMessage.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
            'bg-blue-50 text-blue-800 border border-blue-200'
          }`}>
            {statusMessage.type === 'success' && <CheckCircle size={20} />}
            {statusMessage.type === 'error' && <AlertCircle size={20} />}
            {statusMessage.type === 'info' && <Settings size={20} />}
            <span className="font-semibold">{statusMessage.message}</span>
          </div>
        )}

        {/* EmailJS Configuration Section */}
        <SettingsAccordion
          title="EmailJS Configuratie"
          icon={<Settings size={20} />}
          defaultOpen={false}
        >
          <EmailConfigSection
            settings={settings}
            onSettingsChange={setSettings}
            onShowMessage={showMessage}
            onRefresh={loadSettings}
            isAccordion={true}
          />
        </SettingsAccordion>

        {/* Email Reports Section */}
        <SettingsAccordion
          title="Email Rapporten"
          icon={<Mail size={20} />}
          defaultOpen={false}
        >
          <EmailReportsSection
            settings={settings}
            onSettingsChange={setSettings}
            onShowMessage={showMessage}
            isAccordion={true}
          />
        </SettingsAccordion>

        {/* Report Status Section */}
        <SettingsAccordion
          title="Rapport Status & Geschiedenis"
          icon={<BarChart3 size={20} />}
          defaultOpen={false}
        >
          <ReportStatusSection
            settings={settings}
            onRefresh={loadSettings}
            isAccordion={true}
          />
        </SettingsAccordion>

        {/* Save/Reset Buttons */}
        <div className="card p-4 sm:p-5 mt-6">
          <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
            <button
              onClick={resetSettings}
              disabled={isSaving}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw size={16} />
              Reset
            </button>
            <button
              onClick={saveSettings}
              disabled={isSaving}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Save size={16} />
              )}
              {isSaving ? 'Opslaan...' : 'Instellingen Opslaan'}
            </button>
          </div>
        </div>
      </main>
    </div>
  )
}
